import { ReactComponent as CrossMark } from "../../shared/assets/crossMarkGreen.svg";
import { ReactComponent as List } from "../../shared/assets/list.svg";
import { ReactComponent as TickMark } from "../../shared/assets/tickMarkGreen.svg";
import { themeAtom } from "../../shared/atoms";
import CustomMenuList from "../../shared/components/CustomMenuList";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { ReactSelectStyling } from "../../tech-form/utils/TechUtils";
import { ReactComponent as AddTaskIcon } from "../assets/addTaskIcon.svg";
import { OptionType, TaskItem } from "../models/delivery-plan.interfaces";
import { useAtomValue } from "jotai";
import { SetStateAction } from "react";
import Select, { SingleValue } from "react-select";
import { twMerge } from "tailwind-merge";

// TODO Needs more work to integrate some of the external dependencies
export function PlanChecklist(
  isLoading: boolean,
  checkedCount: number,
  items: TaskItem[],
  CheckedItems: { [key: number]: boolean },
  handleCheckboxChange: (taskId: number, item: TaskItem) => void,
  extractInitials: (fullName: string | null) => { firstNameInitial: string; lastNameInitial: string },
  formatDate: (dateString: string) => string,
  ResetToPreset: (item: TaskItem) => Promise<void>,
  DeleteItem: (item: TaskItem) => Promise<void>,
  showInput: boolean,
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  AddCustomInputTask: () => Promise<void>,
  customInputViewCancel: () => void,
  options: OptionType[],
  setShowInput: (value: SetStateAction<boolean>) => void,
  handleSelectChange: (option: SingleValue<OptionType>) => void,
  selectedOption: OptionType | null,
  handleSelectAddButtonClick: () => Promise<void>,
) {
  const theme = useAtomValue(themeAtom);
  const selectStyles = ReactSelectStyling();

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div className="voyage-checklist-body">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className={twMerge(
                "flex h-9 w-9 items-center justify-center rounded-full",
                theme.myVesselsTable.tailwind.emptyStateIconBg,
              )}
            >
              <List className={twMerge("h-4 w-4", theme.home.tailwind.spinner)} />
            </span>
            <p className={twMerge("text-[18px] font-semibold leading-7", theme.home.tailwind.textDefault)}>Checklist</p>
          </div>
          <span
            className={twMerge(
              "rounded-full border px-3 py-1 text-sm font-medium leading-5",
              theme.voyageChecklist.completionBadgeBg,
              theme.voyageChecklist.completionBadgeBorder,
              theme.voyageChecklist.completionBadgeText,
            )}
          >
            {checkedCount}/{items?.length} completed
          </span>
        </div>

        <div className="pt-6">
          <div
            className={twMerge(
              "max-h-[360px] overflow-y-auto pr-1",
              "[&::-webkit-scrollbar]:w-1.5",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              theme.uwhcAssistant.chatUi.mainScrollbar,
              theme.uwhcAssistant.chatUi.mainScrollbarTrack,
            )}
          >
            <div className="flex flex-col gap-3">
              {items.map((item) => {
                const isChecked = CheckedItems[item.TASK_ID] || false;

                return (
                  <div
                    key={item.TASK_ID}
                    className={twMerge(
                      "flex items-start gap-3 rounded-[12px] border px-4 py-4",
                      isChecked
                        ? twMerge(theme.voyageChecklist.itemCheckedBorder, theme.voyageChecklist.itemCheckedBg)
                        : twMerge(theme.myVesselsTable.tailwind.tableBorder, theme.home.tailwind.panelBackground),
                    )}
                  >
                    <input
                      type="checkbox"
                      id={`deliveryCheckbox-${item.TASK_ID}`}
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(item.TASK_ID, item)}
                      className="hidden"
                    />
                    <label
                      htmlFor={`deliveryCheckbox-${item.TASK_ID}`}
                      className={twMerge(
                        "mt-0.5 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-[1.6px]",
                        isChecked
                          ? twMerge(
                              theme.voyageChecklist.itemCheckedCheckboxBorder,
                              theme.voyageChecklist.itemCheckedCheckboxBg,
                            )
                          : twMerge(theme.uwhcAssistant.tableBorder, theme.uwhcAssistant.aiReplyBg),
                      )}
                    >
                      {isChecked && <TickMark className="h-3.5 w-3.5" />}
                    </label>
                    <div className="min-w-0 flex-1">
                      <p
                        className={twMerge(
                          "text-sm leading-7",
                          theme.home.tailwind.textDefault,
                          isChecked && twMerge(theme.voyageChecklist.itemCheckedText, "line-through"),
                          item.TASK_TYPE !== "Standard" && !isChecked && theme.vesselDetails.info,
                        )}
                      >
                        {item.TASK_DESCRIPTION}
                      </p>

                      <div
                        className={twMerge("flex flex-wrap items-center gap-2 pt-2 text-xs", theme.vesselDetails.info)}
                      >
                        {item.LAST_UPDATED_GMT && item.OPERATOR && (
                          <>
                            <p>Task completed by:</p>
                            <p
                              className={twMerge(
                                "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold uppercase text-green",
                                theme.myVesselsTable.tailwind.emptyStateIconBg,
                              )}
                            >
                              {`${extractInitials(item.OPERATOR).firstNameInitial}${extractInitials(item.OPERATOR).lastNameInitial}`}
                            </p>
                            <p className="capitalize">{item.OPERATOR}</p>
                            <p>{formatDate(item.LAST_UPDATED_GMT)}</p>
                          </>
                        )}
                        {item.TASK_TYPE !== "Standard" && item.OPERATOR && item.LAST_UPDATED_GMT && (
                          <button className="font-semibold text-green" onClick={() => ResetToPreset(item)}>
                            Reset to preset
                          </button>
                        )}
                        {item.TASK_TYPE !== "Standard" && (
                          <button className="font-semibold text-green" onClick={() => DeleteItem(item)}>
                            Delete task
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="pt-4">
            {showInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Add task"
                  className={twMerge(
                    "h-10 flex-1 rounded-[10px] border px-4 text-sm placeholder:text-[#8B8B8B] focus:outline-none",
                    theme.uwhcAssistant.tableBorder,
                    theme.uwhcAssistant.inputBg,
                    theme.uwhcAssistant.inputText,
                  )}
                  onChange={handleInputChange}
                />
                <button
                  onClick={AddCustomInputTask}
                  className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#0B9B68]"
                >
                  <TickMark className="h-4 w-4" />
                </button>
                <button
                  onClick={customInputViewCancel}
                  className={twMerge(
                    "flex h-10 w-10 items-center justify-center rounded-[10px] border",
                    theme.uwhcAssistant.tableBorder,
                    theme.home.tailwind.panelBackground,
                  )}
                >
                  <CrossMark className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Select
                  className="flex-1"
                  menuPortalTarget={document.body}
                  options={options}
                  components={{
                    MenuList: (props: any) => (
                      // Use the CustomMenuList component and pass setShowInput prop
                      <CustomMenuList {...props} setShowInput={setShowInput} />
                    ),
                  }}
                  onChange={handleSelectChange}
                  value={selectedOption}
                  isSearchable={false}
                  placeholder="Select an option"
                  styles={selectStyles}
                  classNamePrefix="rs"
                />
                <button
                  onClick={handleSelectAddButtonClick}
                  className={twMerge(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] focus:outline-none",
                    theme.uwhcAssistant.chatUi.sendButton,
                    theme.uwhcAssistant.chatUi.sendButtonHover,
                  )}
                  aria-label="Add task"
                  title="Add task"
                >
                  <AddTaskIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
}
